import { useEffect, useState } from "react";

/**
 * Module access: the canonical answer to "which modules exist and which may
 * this user see?". Owns the `ModuleInfo` type, the enabled-modules fetcher,
 * role checks, and the routing-aware current-module hook. Used by the shell,
 * header, and home — previously three drifted copies.
 */

export interface ModuleInfo {
  name: string;
  title: string;
  exact: boolean;
  icon: string;
  path: string;
  src: string;
  rootModule: boolean;
  code: string;
  cardVisible?: boolean;
}

interface EnabledModulesResponse {
  enabledModules: string[];
}

/** Minimal account shape — callers pass `getAccount()`, never the auth module. */
export interface AccountLike {
  idTokenClaims?: { roles?: unknown } | null;
}

export const getRoles = (account: AccountLike | null | undefined): string[] => {
  const roles = account?.idTokenClaims?.roles;
  return Array.isArray(roles) ? roles.filter((role): role is string => typeof role === "string") : [];
};

export const hasRole = (account: AccountLike | null | undefined, role: string): boolean =>
  getRoles(account).includes(role);

export const isAdmin = (account: AccountLike | null | undefined): boolean =>
  hasRole(account, "Admin");

export interface FetchDeps {
  baseUrl: string;
  getToken: () => Promise<string>;
  fetchFn?: typeof fetch;
}

const isEnabledModulesResponse = (value: unknown): value is EnabledModulesResponse => {
  if (!value || typeof value !== "object") {
    return false;
  }
  const { enabledModules } = value as Partial<EnabledModulesResponse>;
  return (
    Array.isArray(enabledModules) &&
    enabledModules.every((moduleCode) => typeof moduleCode === "string")
  );
};

/**
 * Fetch the enabled module codes for one group. Single contract: resolves
 * codes, throws on transport failure, non-2xx status, or invalid payload.
 *
 * Results are cached per (baseUrl, group) for `GRANTS_TTL_MS` with
 * in-flight deduplication, so the shell, header, and home share one
 * request per group instead of re-fetching on every mount/navigation.
 * Callers passing a custom `fetchFn` bypass the cache (tests/seams).
 */
export const fetchEnabledCodes = async (
  group: string,
  { baseUrl, getToken, fetchFn = fetch }: FetchDeps,
): Promise<string[]> => {
  if (fetchFn === fetch) {
    const cached = readCodesCache(baseUrl, group);
    if (cached) {
      return cached;
    }
    const inFlight = codesInFlight.get(codesCacheKey(baseUrl, group));
    if (inFlight) {
      return inFlight;
    }
    const pending = fetchEnabledCodesUncached(group, { baseUrl, getToken, fetchFn });
    codesInFlight.set(codesCacheKey(baseUrl, group), pending);
    try {
      const codes = await pending;
      writeCodesCache(baseUrl, group, codes);
      return codes;
    } finally {
      codesInFlight.delete(codesCacheKey(baseUrl, group));
    }
  }
  return fetchEnabledCodesUncached(group, { baseUrl, getToken, fetchFn });
};

/** Grants cache TTL: grants change rarely per session; 60s bounds staleness. */
const GRANTS_TTL_MS = 60_000;

type CodesCacheEntry = { expires: number; value: string[] };

const codesCache = new Map<string, CodesCacheEntry>();
const codesInFlight = new Map<string, Promise<string[]>>();

const codesCacheKey = (baseUrl: string, group: string): string => `${baseUrl}::${group}`;

const readCodesCache = (baseUrl: string, group: string): string[] | undefined => {
  const entry = codesCache.get(codesCacheKey(baseUrl, group));
  if (!entry) {
    return undefined;
  }
  if (Date.now() > entry.expires) {
    codesCache.delete(codesCacheKey(baseUrl, group));
    return undefined;
  }
  return entry.value;
};

const writeCodesCache = (baseUrl: string, group: string, value: string[]): void => {
  codesCache.set(codesCacheKey(baseUrl, group), { expires: Date.now() + GRANTS_TTL_MS, value });
};

/** Test-only seam: drop grants caches so the next fetch re-hits the network. */
export const resetModuleAccessCache = (): void => {
  codesCache.clear();
  codesInFlight.clear();
  homeGrantCache.clear();
  homeGrantInFlight.clear();
};

const fetchEnabledCodesUncached = async (
  group: string,
  { baseUrl, getToken, fetchFn = fetch }: FetchDeps,
): Promise<string[]> => {
  const token = await getToken();
  const modulesUrl = new URL(`${baseUrl}/api/npl/profileconfigurations/modules`);
  modulesUrl.searchParams.set("group", group);

  const response = await fetchFn(modulesUrl, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    throw new Error(`Module discovery failed for group "${group}" with status ${response.status}`);
  }

  const payload: unknown = await response.json();
  if (!isEnabledModulesResponse(payload)) {
    throw new Error(`Module discovery returned an invalid payload for group "${group}"`);
  }

  return payload.enabledModules;
};

/** Fetch the home-dashboard grants object for the account menu (cached like module codes). */
export const fetchHomeGrant = async <T = unknown>({
  baseUrl,
  getToken,
  fetchFn = fetch,
}: FetchDeps): Promise<T> => {
  if (fetchFn === fetch) {
    const cached = readHomeGrantCache<T>(baseUrl);
    if (cached !== undefined) {
      return cached;
    }
    const inFlight = homeGrantInFlight.get(baseUrl) as Promise<T> | undefined;
    if (inFlight) {
      return inFlight;
    }
    const pending = fetchHomeGrantUncached<T>({ baseUrl, getToken, fetchFn });
    homeGrantInFlight.set(baseUrl, pending);
    try {
      const grants = await pending;
      writeHomeGrantCache(baseUrl, grants);
      return grants;
    } finally {
      homeGrantInFlight.delete(baseUrl);
    }
  }
  return fetchHomeGrantUncached<T>({ baseUrl, getToken, fetchFn });
};

type HomeGrantCacheEntry = { expires: number; value: unknown };

const homeGrantCache = new Map<string, HomeGrantCacheEntry>();
const homeGrantInFlight = new Map<string, Promise<unknown>>();

const readHomeGrantCache = <T>(baseUrl: string): T | undefined => {
  const entry = homeGrantCache.get(baseUrl);
  if (!entry) {
    return undefined;
  }
  if (Date.now() > entry.expires) {
    homeGrantCache.delete(baseUrl);
    return undefined;
  }
  return entry.value as T;
};

const writeHomeGrantCache = (baseUrl: string, value: unknown): void => {
  homeGrantCache.set(baseUrl, { expires: Date.now() + GRANTS_TTL_MS, value });
};

const fetchHomeGrantUncached = async <T = unknown>({
  baseUrl,
  getToken,
  fetchFn = fetch,
}: FetchDeps): Promise<T> => {
  const token = await getToken();
  const response = await fetchFn(`${baseUrl}/api/npl/profileconfigurations/home`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });
  if (!response.ok) {
    throw new Error(`Home grant request failed with status ${response.status}`);
  }
  return (await response.json()) as T;
};

export const defaultModule: ModuleInfo = {
  name: "Header",
  title: "",
  exact: false,
  icon: "",
  path: "",
  src: "",
  rootModule: false,
  code: "",
};

export const findCurrentModule = (modules: ModuleInfo[], pathname: string): ModuleInfo => {
  const targetModule = modules.find((module) => {
    if (module.exact) {
      return pathname === module.path;
    }
    return pathname.startsWith(module.path);
  });
  return targetModule ?? defaultModule;
};

export const selectMainModules = (modules: ModuleInfo[]): ModuleInfo[] =>
  modules.filter((module) => module.rootModule);

/**
 * Current module + main modules, re-resolved on every single-spa routing
 * event so the value never goes stale after in-app navigation.
 */
export const useCurrentModule = (modules: ModuleInfo[]): [ModuleInfo, ModuleInfo[]] => {
  const [pathname, setPathname] = useState(window.location.pathname);

  useEffect(() => {
    const handleRoutingEvent = () => {
      setPathname(window.location.pathname);
    };
    window.addEventListener("single-spa:routing-event", handleRoutingEvent);
    return () => {
      window.removeEventListener("single-spa:routing-event", handleRoutingEvent);
    };
  }, []);

  return [findCurrentModule(modules, pathname), selectMainModules(modules)];
};
